import { useTranslation } from "react-i18next";
import { Bot, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { AssistantConversation } from "@/components/chat/AssistantConversation";

interface AssistantDockedPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Desktop AI assistant — docked right rail/panel (TES-1128, WP4).
 *
 * DS-v2 reference: screen-05 "AI Assistant Sidebar (Fixed/Docked)" — a
 * `w-assistant-panel-width` (320px) fixed aside on the right edge with a
 * `bg-primary-container` header (smart-toy + "TP2 Assistant" + online dot) over
 * a chat body and input. Collapsible to a thin rail per screen-01/07
 * ("collapsed AI rail, hover-expand"): when collapsed it renders a slim
 * `w-12` strip with a pulsing indicator that re-opens the panel.
 *
 * Rendered only on `lg+` (desktop); the mobile FAB + bottom sheet covers
 * smaller viewports.
 */
export function AssistantDockedPanel({ open, onOpenChange }: AssistantDockedPanelProps) {
  const { t } = useTranslation();

  // Collapsed rail (screen-01/07): thin strip that reopens the panel.
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => onOpenChange(true)}
        aria-label={t("aiAssistant.open")}
        data-testid="assistant-rail"
        className={cn(
          "group relative hidden w-12 shrink-0 flex-col items-center gap-3 border-l border-outline-variant",
          "bg-surface-container-low py-4 transition-colors hover:bg-surface-container lg:flex",
        )}
      >
        <span className="relative">
          <Bot className="h-5 w-5 animate-pulse text-primary" />
          <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full border border-white bg-secondary-container" />
        </span>
        <span className="mt-2 rotate-180 text-[11px] font-bold uppercase tracking-wide text-on-surface-variant [writing-mode:vertical-rl]">
          {t("aiAssistant.title")}
        </span>
      </button>
    );
  }

  return (
    <aside
      data-testid="assistant-panel"
      aria-label={t("aiAssistant.title")}
      className={cn(
        "hidden w-assistant-panel-width shrink-0 flex-col border-l border-outline-variant bg-card lg:flex",
      )}
    >
      {/* Header — DS-v2 primary-container bar */}
      <div className="flex items-center justify-between border-b border-outline-variant bg-primary-container px-4 py-3 text-on-primary-container">
        <div className="flex items-center gap-2">
          <span className="relative">
            <Bot className="h-5 w-5 animate-pulse" />
            <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full border border-primary-container bg-secondary-container" />
          </span>
          <span className="font-display text-sm font-semibold">{t("aiAssistant.title")}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-on-primary-container hover:bg-white/10"
            onClick={() => onOpenChange(false)}
            aria-label={t("aiAssistant.collapse")}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <AssistantConversation className="flex flex-1 flex-col overflow-hidden" />
    </aside>
  );
}
